import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeElementsOptions } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Form, Modal, Nav, Tab } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { ArrowRepeat, CheckCircleFill, CreditCard2Front, InfoCircleFill, XCircleFill } from "react-bootstrap-icons";
import { useAtom, useAtomValue } from "jotai";
import { maskAtom, PageMaskOpts, groupParkerPendingCheckoutAtom } from "./Data/Atoms";
import { Api, SetupIntentCreateResp } from "./Data/Api";
import { stripePromise } from "./Data/ApiStripe";
import { appearanceOptions, stripeFonts } from "./Controls/StripeAppearance";
import { PaymentMethodResp, SubListEntry } from "./Data/ApiTypes";
import { SubscriptionStatus } from "./Data/ApiTransport";
import { connectUrl } from "./Data/WebUrl";
import { countPaymentMethodSubscriptions, showSubscriptionStatus } from "./Data/Subscription";
import { cents, showMoney } from "./Data/Money";
import { facilityImg } from "./Data/Common";

export type PaymentMethodModalMode = "add" | "modify" | "move";

interface AddMethodModalProps {
    show:                     boolean;
    onHide:                   () => void;
    subscriptions:            SubListEntry[];
    paymentMethods:           PaymentMethodResp[];
    onSuccess:                () => void;
    mode?:                    PaymentMethodModalMode;
    initialSubscriptionIds?:  number[];
    /** Move mode: all subscription IDs on source PM (includes usage-based for display). */
    sourceSubscriptionIds?:   number[];
    sourcePaymentMethodId?:   string;
    contextPlan?:             { name: string; facility: string };
    /** Usage-based plans: card saved + new card only (no ACH). */
    cardOnly?:                boolean;
    /** Skip bulk plan picker; open payment step directly (single-plan context). */
    skipPlanSelect?:          boolean;
}

type Step = "select" | "payment" | "success";
type PmTab  = "saved" | "card" | "ach";

function getExistingPm( sub: SubListEntry, paymentMethods: PaymentMethodResp[] ): PaymentMethodResp | undefined {
    return paymentMethods.find( pm => pm.subscriptions.some( s => s.subscriptionId === sub.subscriptionId ) );
}

export function pmLabel( pm: PaymentMethodResp ): string {
    if( pm.card )          return `${pm.card.brand.charAt(0).toUpperCase() + pm.card.brand.slice(1)} ••••${pm.card.last4Digits}`;
    if( pm.usBankAccount ) return `${pm.usBankAccount.bank} ••••${pm.usBankAccount.last4Digits}`;
    return pm.type;
}

function selectedRequireAch( subscriptions: SubListEntry[], selectedIds: number[] ): boolean {
    return subscriptions
        .filter( s => selectedIds.includes( s.subscriptionId ) )
        .some( s => {
            const monthly = s.cachedMrr > 0 ? s.cachedMrr : s.price;
            return s.facility.maxCreditCardAmountInCents != null
                && monthly != null
                && monthly > s.facility.maxCreditCardAmountInCents;
        } );
}

function modalTitle( mode: PaymentMethodModalMode ): string {
    switch( mode ) {
        case "modify": return "Add or Modify Payment Method";
        case "move":   return "Move Subscriptions to Another Payment Method";
        default:       return "Add or Replace Payment Method";
    }
}

// ── Stepper ──────────────────────────────────────────────────────────────────

function StepperHeader( { step, hidePlanSelect }: { step: Step; hidePlanSelect?: boolean } ) {
    const activeSelect  = step === "select";
    const activePayment = step === "payment" || step === "success";
    const pink = "#D61F84";
    const activeBg = pink;
    if( hidePlanSelect ) return null;
    return (
        <div className="d-flex align-items-center gap-2" style={{ fontSize: "0.8rem", flexWrap: "nowrap", overflowX: "auto" }}>
            <span className={`d-flex align-items-center gap-1 ${activeSelect ? "fw-semibold" : "text-secondary"}`}
                  style={activeSelect ? { color: pink } : undefined}>
                <span className="rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: 22, height: 22, fontSize: "0.75rem", background: activeSelect ? activeBg : "#e9ecef", color: activeSelect ? "#fff" : "#6c757d" }}>
                    1
                </span>
                Select plans
            </span>
            <span className="text-secondary">→</span>
            <span className={`d-flex align-items-center gap-1 ${activePayment ? "fw-semibold" : "text-secondary"}`}
                  style={activePayment ? { color: pink } : undefined}>
                <span className="rounded-circle d-inline-flex align-items-center justify-content-center"
                      style={{ width: 22, height: 22, fontSize: "0.75rem", background: activePayment ? activeBg : "#e9ecef", color: activePayment ? "#fff" : "#6c757d" }}>
                    2
                </span>
                Payment method
            </span>
        </div>
    );
}

// ── Step 1: subscription selector ───────────────────────────────────────────

interface SelectStepProps {
    mode:                    PaymentMethodModalMode;
    subscriptions:           SubListEntry[];
    paymentMethods:          PaymentMethodResp[];
    selected:                Set<number>;
    onToggle:                ( id: number ) => void;
    onContinue:              () => void;
    onCancel:                () => void;
    initialSubscriptionIds?: number[];
    sourceSubscriptionIds?:  number[];
    contextPlan?:            { name: string; facility: string };
}

function SelectStep( {
    mode, subscriptions, paymentMethods, selected, onToggle, onContinue, onCancel,
    initialSubscriptionIds, sourceSubscriptionIds, contextPlan,
}: SelectStepProps ) {
    const moveSourceIds = sourceSubscriptionIds ?? [];
    const hasMoveSource = mode === "move" && moveSourceIds.length > 0;
    const hasPreselection = mode === "move"
        ? hasMoveSource
        : ( initialSubscriptionIds?.length ?? 0 ) > 0;
    const [showOthers, setShowOthers] = useState( false );

    const relevantStatuses = ( s: SubListEntry ) =>
        s.status === SubscriptionStatus.Active;

    const selectable  = subscriptions.filter( s => relevantStatuses( s ) && !s.isUsageBasedBilling );
    const usageBased  = subscriptions.filter( s => relevantStatuses( s ) &&  s.isUsageBasedBilling );

    const isOnSourceCard = ( subId: number ) =>
        mode === "move"
            ? moveSourceIds.includes( subId )
            : ( initialSubscriptionIds?.includes( subId ) ?? false );

    const hiddenCount = hasPreselection && !showOthers
        ? selectable.filter( s => !isOnSourceCard( s.subscriptionId ) ).length
        : 0;
    const visibleSelectable = hasPreselection && !showOthers
        ? selectable.filter( s => isOnSourceCard( s.subscriptionId ) )
        : selectable;
    const visibleUsageBased = mode === "move"
        ? ( showOthers ? [] : usageBased.filter( s => moveSourceIds.includes( s.subscriptionId ) ) )
        : mode === "add"
            ? usageBased
            : [];

    const infoBanner = hiddenCount > 0
        ? mode === "modify"
            ? "This plan is pre-selected. Expand below to apply to other plans."
            : mode === "move"
                ? "Subscriptions currently on this card are pre-selected. Expand below to include other plans."
                : null
        : null;

    return <>
        <Modal.Header className="border-0 pb-0 px-3 pt-3 d-flex flex-column align-items-stretch gap-1">
            <StepperHeader step="select" />
            <div className="mt-1">
                <Modal.Title className="fs-6 fw-semibold">{modalTitle( mode )}</Modal.Title>
                {contextPlan && (
                    <div className="text-secondary mt-1" style={{ fontSize: "0.82rem" }}>
                        {contextPlan.name} · {contextPlan.facility}
                    </div>
                )}
            </div>
        </Modal.Header>
        <Modal.Body className="pt-2 px-3">
            {infoBanner && (
                <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3" role="alert" style={{ fontSize: "0.85rem" }}>
                    <InfoCircleFill className="flex-shrink-0 mt-1" />
                    <span>{infoBanner}</span>
                </div>
            )}

            {mode === "add" && (
                <p className="text-secondary" style={{ fontSize: "0.88rem" }}>
                    Select the plans you'd like to apply this payment method to.
                </p>
            )}

            {selectable.length === 0 && usageBased.length === 0 && (
                <div className="text-center text-muted py-4" style={{ fontSize: "0.9rem" }}>
                    <CreditCard2Front size={28} className="mb-2 d-block mx-auto opacity-50" />
                    No active plans found. Payment methods are managed per plan.
                </div>
            )}

            <div className="d-flex flex-column gap-2">
                {visibleSelectable.map( sub => {
                    const existing   = getExistingPm( sub, paymentMethods );
                    const isPending  = sub.isPendingPaymentMethodVerification;
                    const isChecked  = selected.has( sub.subscriptionId );
                    const price      = sub.cachedMrr > 0 ? sub.cachedMrr : sub.price;

                    return (
                        <div
                            key={sub.subscriptionId}
                            role={isPending ? undefined : "button"}
                            onClick={isPending ? undefined : () => onToggle( sub.subscriptionId )}
                            className={`d-flex align-items-start gap-2 p-2 rounded-3 border ${isPending ? "border-secondary opacity-75" : ""}`}
                            style={{ cursor: isPending ? "not-allowed" : "pointer", transition: "all 0.15s ease", borderColor: isPending ? undefined : isChecked ? "#D61F84" : "#dee2e6", background: (!isPending && isChecked) ? "rgba(214,31,132,0.07)" : undefined }}
                        >
                            <Form.Check
                                type="checkbox"
                                checked={isChecked}
                                disabled={isPending}
                                onChange={() => !isPending && onToggle( sub.subscriptionId )}
                                onClick={e => e.stopPropagation()}
                                className="mt-1 flex-shrink-0"
                                style={!isChecked ? { backgroundColor: "transparent", borderColor: "#6c757d" } : undefined}
                            />
                            <img
                                src={facilityImg( sub.facility.listImageId )}
                                alt=""
                                className="rounded-2 flex-shrink-0 d-none d-sm-block"
                                style={{ width: 40, height: 40, objectFit: "cover" }}
                            />
                            <div className="flex-grow-1 min-w-0">
                                <div className="d-flex align-items-start justify-content-between gap-1">
                                    <div className="fw-semibold lh-sm" style={{ fontSize: "0.9rem", wordBreak: "break-word" }}>
                                        {sub.plan.name}
                                    </div>
                                    <Badge
                                        bg="secondary"
                                        className="flex-shrink-0 fw-normal ms-1"
                                        style={{ fontSize: "0.7rem", ...(isChecked && !isPending ? { background: "#D61F84", color: "#fff" } : {}) }}
                                    >
                                        {showSubscriptionStatus( sub.status, sub.start )}
                                    </Badge>
                                </div>
                                <div className="text-secondary text-truncate" style={{ fontSize: "0.82rem" }}>
                                    {sub.facility.name}
                                    {price != null && <> &middot; {showMoney( cents( price ) )}/mo</>}
                                </div>
                                {isPending ? (
                                    <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: "0.76rem", color: "#fd7e14" }}>
                                        <InfoCircleFill size={11} className="flex-shrink-0" />
                                        Pending bank account verification
                                    </div>
                                ) : existing ? (
                                    <div className="mt-1">
                                        <Badge bg="warning" text="dark" className="fw-normal" style={{ fontSize: "0.73rem" }}>
                                            Currently billed via {pmLabel( existing )}
                                        </Badge>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: "0.76rem", color: "#D61F84" }}>
                                        <ArrowRepeat size={11} className="flex-shrink-0" />
                                        Selecting this plan will enroll it in auto-renewal
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                } )}

                {visibleUsageBased.map( sub => {
                    const price = sub.cachedMrr > 0 ? sub.cachedMrr : sub.price;
                    return (
                        <div
                            key={sub.subscriptionId}
                            className="d-flex align-items-start gap-2 p-2 rounded-3 border border-secondary opacity-50"
                            style={{ cursor: "not-allowed" }}
                        >
                            <div className="mt-1 flex-shrink-0" style={{ width: 16 }} />
                            <img
                                src={facilityImg( sub.facility.listImageId )}
                                alt=""
                                className="rounded-2 flex-shrink-0 d-none d-sm-block"
                                style={{ width: 40, height: 40, objectFit: "cover" }}
                            />
                            <div className="flex-grow-1 min-w-0">
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                    <div className="fw-semibold lh-sm text-truncate" style={{ fontSize: "0.92rem" }}>
                                        {sub.plan.name}
                                    </div>
                                    <Badge bg="secondary" className="flex-shrink-0 fw-normal" style={{ fontSize: "0.73rem" }}>
                                        {showSubscriptionStatus( sub.status, sub.start )}
                                    </Badge>
                                </div>
                                <div className="text-secondary text-truncate" style={{ fontSize: "0.82rem" }}>
                                    {sub.facility.name}
                                    {price != null && <> &middot; {showMoney( cents( price ) )}/mo</>}
                                </div>
                                <div className="mt-1" style={{ fontSize: "0.85rem" }}>
                                    Payment method must be managed through the plan.
                                </div>
                            </div>
                        </div>
                    );
                } )}
            </div>

            {hiddenCount > 0 && (
                <button
                    type="button"
                    className="btn btn-link p-0 mt-2 text-decoration-none"
                    style={{ fontSize: "0.82rem", color: "#D61F84" }}
                    onClick={() => setShowOthers( v => !v )}
                >
                    {showOthers ? "Hide other plans" : `Show other plans (${hiddenCount})`}
                </button>
            )}

            {mode !== "modify" && selectable.some( sub => selected.has( sub.subscriptionId ) && getExistingPm( sub, paymentMethods ) ) && (
                <div className="mt-3 p-3 rounded-3 border border-warning bg-warning bg-opacity-10" style={{ fontSize: "0.83rem" }}>
                    <strong>Note:</strong> Subscriptions that already have a payment method will have it replaced.
                </div>
            )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
            <Button size="sm" onClick={onCancel}>
                Cancel
            </Button>
            <Button
                className="btn-main px-4"
                size="sm"
                onClick={onContinue}
                disabled={selected.size === 0}
            >
                Continue{selected.size > 0 ? ` (${selected.size})` : ""}
            </Button>
        </Modal.Footer>
    </>;
}

// ── Step 2: Stripe payment element ──────────────────────────────────────────

interface PaymentStepInnerProps {
    selectedIds:                number[];
    achOnly:                    boolean;
    maxCreditCardAmountInCents: number | null;
    achForced:                  boolean;
    formId:                     string;
    onSuccess:                  () => void;
}

function PaymentStepInner( { selectedIds, achOnly, maxCreditCardAmountInCents, achForced, formId, onSuccess }: PaymentStepInnerProps ) {
    const stripe   = useStripe();
    const elements = useElements();
    const [, setMask] = useAtom( maskAtom );
    const [err, setErr] = useState<string>();

    const maskOpts: PageMaskOpts = {
        showMask: true,
        header:   <div className="h2">Processing</div>,
        subText:  "Do not close your browser",
    };

    const handleSubmit = async ( e: React.FormEvent ) => {
        e.preventDefault();
        if( !stripe || !elements ) return;

        setErr( undefined );
        setMask( maskOpts );

        const result = await stripe.confirmSetup( {
            elements,
            confirmParams: {
                return_url: connectUrl(
                    `/user/billing/add/success?` +
                    `returnTo=${encodeURIComponent( "/user/billing" )}` +
                    `&returnLabel=${encodeURIComponent( "Back to Payment Methods" )}`
                ),
            },
            redirect: "if_required",
        } );

        setMask( undefined );

        if( result.error ) {
            setErr( result.error.message ?? "Something went wrong. Please try again." );
        } else {
            onSuccess();
        }
    };

    return <>
        {selectedIds.length > 0 && (
            <p className="text-secondary mb-3" style={{ fontSize: "0.88rem" }}>
                This will be applied to <strong>{selectedIds.length} plan{selectedIds.length !== 1 ? "s" : ""}</strong>.
            </p>
        )}
        {achForced && maxCreditCardAmountInCents !== null && (
            <div className="small text-muted bg-light border rounded-3 px-3 py-2 mb-3">
                Monthly payments over {showMoney( cents( maxCreditCardAmountInCents ) )} require ACH. Add a bank account to continue.
            </div>
        )}
        <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-3" role="alert" style={{ fontSize: "0.85rem" }}>
            <InfoCircleFill className="flex-shrink-0 mt-1" />
            <span>
                If adding a new method, check <b>"Save payment details for future purchases"</b> to make it available across all your SpotSync transactions.
            </span>
        </div>
        {err && (
            <div className="alert alert-danger d-flex align-items-center gap-2 py-2" style={{ fontSize: "0.88rem" }}>
                <XCircleFill className="flex-shrink-0" />
                {err}
            </div>
        )}
        <form id={formId} onSubmit={handleSubmit}>
            <PaymentElement />
        </form>
    </>;
}

interface StripeTabPanelProps {
    createResp:  SetupIntentCreateResp;
    selectedIds: number[];
    achForced:   boolean;
    formId:      string;
    onSuccess:   () => void;
}

function StripeTabPanel( { createResp, selectedIds, achForced, formId, onSuccess }: StripeTabPanelProps ) {
    const options: StripeElementsOptions = {
        clientSecret:                createResp.clientSecret,
        customerSessionClientSecret: createResp.customerSessionClientSecret,
        fonts:                       stripeFonts,
        appearance:                  appearanceOptions,
    };

    return (
        <Elements stripe={stripePromise} options={options}>
            <PaymentStepInner
                selectedIds={selectedIds}
                achOnly={createResp.achOnly}
                maxCreditCardAmountInCents={createResp.maxCreditCardAmountInCents}
                achForced={achForced}
                formId={formId}
                onSuccess={onSuccess}
            />
        </Elements>
    );
}

// ── Step 2: payment method choice ───────────────────────────────────────────

interface PaymentChoiceStepProps {
    mode:                   PaymentMethodModalMode;
    selectedIds:            number[];
    subscriptions:          SubListEntry[];
    paymentMethods:         PaymentMethodResp[];
    sourcePaymentMethodId?: string;
    contextPlan?:           { name: string; facility: string };
    cardOnly?:              boolean;
    onSuccess:              () => void;
    onBack:                 () => void;
}

function PaymentChoiceStep( {
    mode, selectedIds, subscriptions, paymentMethods, sourcePaymentMethodId,
    contextPlan, cardOnly = false, onSuccess, onBack,
}: PaymentChoiceStepProps ) {
    const achRequired = cardOnly ? false : selectedRequireAch( subscriptions, selectedIds );

    // If backend rejects cards for these plans, treat as achRequired
    const [serverForcesAch, setServerForcesAch] = useState( false );
    const effectiveAchRequired = achRequired || serverForcesAch;

    // Exclude source PM (move mode) and, when ACH is required, exclude card PMs
    const savedMethods = useMemo(
        () => paymentMethods.filter( pm => {
            if( pm.paymentMethodId === sourcePaymentMethodId ) return false;
            if( cardOnly && !pm.card ) return false;
            if( effectiveAchRequired && pm.card ) return false;
            return true;
        } ),
        [paymentMethods, sourcePaymentMethodId, effectiveAchRequired, cardOnly],
    );

    const showSavedTab = savedMethods.length > 0;
    const showCardTab  = !effectiveAchRequired;
    const showAchTab   = !cardOnly;

    const firstAvailableTab = useMemo( (): PmTab => {
        if( showSavedTab ) return "saved";
        if( showCardTab ) return "card";
        return "ach";
    }, [showSavedTab, showCardTab] );

    const firstSavedId = savedMethods[0]?.paymentMethodId ?? "";
    const [activeTab, setActiveTab]       = useState<PmTab>( firstAvailableTab );
    const [selectedPmId, setSelectedPmId] = useState<string>( firstSavedId );
    const [cardResp, setCardResp]         = useState<SetupIntentCreateResp>();
    const [achResp, setAchResp]           = useState<SetupIntentCreateResp>();
    const [cardFetchErr, setCardFetchErr] = useState<string>();
    const [achFetchErr, setAchFetchErr]   = useState<string>();
    const [assignErr, setAssignErr]       = useState<string>();
    const [assigning, setAssigning]       = useState( false );

    const loadSetup = useCallback( async ( tab: "card" | "ach" ) => {
        if( tab === "card" ) setCardFetchErr( undefined );
        else setAchFetchErr( undefined );
        const res = await Api.stripeSetupPaymentMethod(
            selectedIds,
            tab === "card" ? { cardOnly: true } : { achOnly: true },
        );
        if( !res.isOk ) {
            if( tab === "card" ) {
                if( cardOnly ) {
                    setCardFetchErr( res.error );
                } else {
                    setServerForcesAch( true );
                    setActiveTab( "ach" );
                }
            } else {
                setAchFetchErr( res.error );
            }
            return;
        }
        if( tab === "card" ) setCardResp( res.value );
        else setAchResp( res.value );
    }, [selectedIds, cardOnly] );

    const handleTabSelect = ( key: string | null ) => {
        if( !key ) return;
        setActiveTab( key as PmTab );
        setAssignErr( undefined );
    };

    useEffect( () => {
        const available: PmTab[] = [];
        if( showSavedTab ) available.push( "saved" );
        if( showCardTab ) available.push( "card" );
        if( showAchTab ) available.push( "ach" );
        if( available.length > 0 && !available.includes( activeTab ) )
            setActiveTab( available[0]! );
    }, [showSavedTab, showCardTab, showAchTab, activeTab] );

    // Proactively verify card is allowed on mount (so the tab only renders if confirmed).
    // ACH tab loads on demand when selected.
    useEffect( () => {
        if( selectedIds.length === 0 ) return;
        if( !achRequired && !cardOnly ) loadSetup( "card" );
        else if( cardOnly ) loadSetup( "card" );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds.length] );

    useEffect( () => {
        if( cardOnly ) return;
        if( activeTab === "ach" && !achResp && !achFetchErr ) loadSetup( "ach" );
    }, [activeTab, achResp, achFetchErr, loadSetup, cardOnly] );

    const handleAssign = async () => {
        if( !selectedPmId ) return;
        setAssigning( true );
        setAssignErr( undefined );
        const res = await Api.stripePaymentMethodAssign( selectedPmId, selectedIds );
        setAssigning( false );
        if( !res.isOk ) {
            setAssignErr( res.error );
            return;
        }
        onSuccess();
    };

    return <>
        <Modal.Header className="border-0 pb-0 px-3 pt-3 d-flex flex-column align-items-stretch gap-1">
            <StepperHeader step="payment" hidePlanSelect={cardOnly} />
            <Modal.Title className="fs-6 fw-semibold mt-1">
                {cardOnly ? "Update payment method" : modalTitle( mode )}
            </Modal.Title>
            {contextPlan && (
                <div className="text-secondary mt-1" style={{ fontSize: "0.82rem" }}>
                    {contextPlan.name} · {contextPlan.facility}
                </div>
            )}
            {cardOnly && (
                <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 mt-2" role="alert" style={{ fontSize: "0.85rem" }}>
                    <InfoCircleFill className="flex-shrink-0 mt-1" />
                    <span>Usage charges at this facility are billed to your card on file.</span>
                </div>
            )}
        </Modal.Header>
        <Modal.Body className="pt-2 px-3">
            <Tab.Container activeKey={activeTab} onSelect={handleTabSelect} mountOnEnter unmountOnExit>
                <Nav variant="tabs" className="mb-3 flex-nowrap" style={{ overflowX: "auto", overflowY: "hidden" }}>
                    {savedMethods.length > 0 && (
                        <Nav.Item>
                            <Nav.Link eventKey="saved">Saved</Nav.Link>
                        </Nav.Item>
                    )}
                    {showCardTab && (
                        <Nav.Item>
                            <Nav.Link eventKey="card">New card</Nav.Link>
                        </Nav.Item>
                    )}
                    {!cardOnly && (
                    <Nav.Item>
                        <Nav.Link eventKey="ach">New bank account</Nav.Link>
                    </Nav.Item>
                    )}
                </Nav>

                <Tab.Content>
                    {savedMethods.length > 0 && (
                        <Tab.Pane eventKey="saved">
                            <p className="text-secondary mb-3" style={{ fontSize: "0.88rem" }}>
                                {cardOnly
                                    ? <>Choose a saved card for <strong>{contextPlan?.facility ?? "this plan"}</strong>.</>
                                    : <>Choose a saved payment method for <strong>{selectedIds.length} plan{selectedIds.length !== 1 ? "s" : ""}</strong>.</>}
                            </p>
                            {assignErr && (
                                <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: "0.88rem" }}>
                                    <XCircleFill className="flex-shrink-0" />
                                    {assignErr}
                                </div>
                            )}
                            <div className="d-flex flex-column gap-2">
                                {savedMethods.map( pm => {
                                    const activeCount = countPaymentMethodSubscriptions( pm.subscriptions );
                                    const isSelected  = selectedPmId === pm.paymentMethodId;
                                    const label = pmLabel( pm );
                                    const detail = pm.card
                                        ? `Expires ${pm.card.expirationMonth}/${pm.card.expirationYear.toString().slice( -2 )}`
                                        : pm.usBankAccount ? pm.usBankAccount.bank : "";
                                    return (
                                        <div
                                            key={pm.paymentMethodId}
                                            role="button"
                                            onClick={() => setSelectedPmId( pm.paymentMethodId )}
                                            className="d-flex align-items-center gap-3 p-3 rounded-3 border"
                                            style={{ cursor: "pointer", borderColor: isSelected ? "#D61F84" : "#dee2e6", background: isSelected ? "rgba(214,31,132,0.07)" : undefined }}
                                        >
                                            <Form.Check
                                                type="radio"
                                                name="saved-pm"
                                                checked={isSelected}
                                                onChange={() => setSelectedPmId( pm.paymentMethodId )}
                                                onClick={e => e.stopPropagation()}
                                                className="flex-shrink-0"
                                            />
                                            <div className="flex-grow-1">
                                                <div className="fw-semibold" style={{ fontSize: "0.92rem" }}>{label}</div>
                                                {detail && (
                                                    <div className="text-secondary" style={{ fontSize: "0.8rem" }}>{detail}</div>
                                                )}
                                            </div>
                                            <span className="px-2 py-1 rounded-pill fw-semibold flex-shrink-0"
                                                  style={{ fontSize: "0.7rem", background: activeCount > 0 ? "#d1fae5" : "#f1f1f1", color: activeCount > 0 ? "#065f46" : "#888" }}>
                                                {activeCount > 0 ? `${activeCount} active` : "Unused"}
                                            </span>
                                        </div>
                                    );
                                } )}
                            </div>
                        </Tab.Pane>
                    )}

                    {!effectiveAchRequired && (
                        <Tab.Pane eventKey="card">
                            {cardFetchErr && (
                                <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: "0.88rem" }}>
                                    <XCircleFill className="flex-shrink-0" />
                                    {cardFetchErr}
                                </div>
                            )}
                            {cardResp && <StripeTabPanel createResp={cardResp} selectedIds={selectedIds} achForced={false} formId="add-pm-form-card" onSuccess={onSuccess} />}
                            {!cardResp && !cardFetchErr && (
                                <div className="text-secondary py-3" style={{ fontSize: "0.88rem" }}>Loading…</div>
                            )}
                        </Tab.Pane>
                    )}

                    {!cardOnly && (
                    <Tab.Pane eventKey="ach">
                        {achFetchErr && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: "0.88rem" }}>
                                <XCircleFill className="flex-shrink-0" />
                                {achFetchErr}
                            </div>
                        )}
                        {achResp && <StripeTabPanel createResp={achResp} selectedIds={selectedIds} achForced={effectiveAchRequired} formId="add-pm-form-ach" onSuccess={onSuccess} />}
                    </Tab.Pane>
                    )}
                </Tab.Content>
            </Tab.Container>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
            <Button size="sm" onClick={onBack}>
                {cardOnly ? "Cancel" : "← Back"}
            </Button>
            {activeTab === "saved" ? (
                <Button
                    className="btn-main px-4"
                    size="sm"
                    disabled={!selectedPmId || assigning}
                    loading={assigning}
                    loadingText="Saving…"
                    onClick={handleAssign}
                >
                    Save
                </Button>
            ) : (
                <Button
                    type="submit"
                    form={activeTab === "card" ? "add-pm-form-card" : "add-pm-form-ach"}
                    className="btn-main px-4"
                    size="sm"
                    disabled={activeTab === "card" ? !cardResp : !achResp }
                >
                    Save Payment Method
                </Button>
            )}
        </Modal.Footer>
    </>;
}

// ── Step 3: Success ──────────────────────────────────────────────────────────

function SuccessStep( { onDone }: { onDone: () => void } ) {
    return (
        <div className="d-flex flex-column align-items-center text-center p-4 gap-3">
            <CheckCircleFill size={48} style={{ color: "#D61F84" }} />
            <div>
                <div className="fw-semibold fs-5 mb-1">Payment method saved!</div>
                <div className="text-secondary" style={{ fontSize: "0.9rem" }}>
                    Your plans have been updated.
                </div>
            </div>
            <Button className="btn-main px-5 mt-1" size="sm" onClick={onDone}>
                Done
            </Button>
        </div>
    );
}

// ── Main modal ───────────────────────────────────────────────────────────────

function isBulkSelectable( sub: SubListEntry, cardOnly: boolean, skipPlanSelect: boolean ): boolean {
    if( sub.isPendingPaymentMethodVerification ) return false;
    if( skipPlanSelect && cardOnly ) return true;
    return !sub.isUsageBasedBilling;
}

export function UserBillingAddMethodModal( {
    show, onHide, subscriptions, paymentMethods, onSuccess,
    mode = "add", initialSubscriptionIds, sourceSubscriptionIds, sourcePaymentMethodId, contextPlan,
    cardOnly = false, skipPlanSelect = false,
}: AddMethodModalProps ) {
    const [step, setStep]         = useState<Step>( skipPlanSelect ? "payment" : "select" );
    const [selected, setSelected] = useState<Set<number>>( new Set() );
    const pendingCheckout         = useAtomValue( groupParkerPendingCheckoutAtom );

    useEffect( () => {
        if( !show ) {
            setStep( skipPlanSelect ? "payment" : "select" );
            setSelected( new Set() );
            return;
        }
        if( initialSubscriptionIds?.length ) {
            const selectableIds = new Set(
                subscriptions
                    .filter( s => initialSubscriptionIds.includes( s.subscriptionId ) && isBulkSelectable( s, cardOnly, skipPlanSelect ) )
                    .map( s => s.subscriptionId ),
            );
            setSelected( selectableIds );
        }
    }, [show, initialSubscriptionIds, subscriptions, skipPlanSelect, cardOnly] );

    const toggleSub = ( id: number ) => {
        setSelected( prev => {
            const next = new Set( prev );
            next.has( id ) ? next.delete( id ) : next.add( id );
            return next;
        } );
    };

    const effectiveSelectedIds = useMemo( () => {
        const fromState = Array.from( selected ).filter( id => {
            const sub = subscriptions.find( s => s.subscriptionId === id );
            return sub && isBulkSelectable( sub, cardOnly, skipPlanSelect );
        } );
        if( fromState.length > 0 ) return fromState;
        if( !initialSubscriptionIds?.length ) return fromState;
        return subscriptions
            .filter( s => initialSubscriptionIds.includes( s.subscriptionId ) && isBulkSelectable( s, cardOnly, skipPlanSelect ) )
            .map( s => s.subscriptionId );
    }, [selected, initialSubscriptionIds, subscriptions, cardOnly, skipPlanSelect] );

    const handleContinue = () => setStep( "payment" );

    if( pendingCheckout ) {
        return (
            <Modal show={show} onHide={onHide} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle( mode )}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0" role="alert">
                        <InfoCircleFill className="flex-shrink-0 mt-1" />
                        <div>
                            <div className="fw-semibold mb-1">Pending checkout</div>
                            <div className="small">
                                Complete or clear your pending group parker checkout before adding a payment method.
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button size="sm" className="btn-main px-4" onClick={onHide}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal show={show} onHide={onHide} centered size="lg" fullscreen="sm-down">
            {step === "select" && (
                <SelectStep
                    mode={mode}
                    subscriptions={subscriptions}
                    paymentMethods={paymentMethods}
                    selected={selected}
                    onToggle={toggleSub}
                    onContinue={handleContinue}
                    onCancel={onHide}
                    initialSubscriptionIds={initialSubscriptionIds}
                    sourceSubscriptionIds={sourceSubscriptionIds}
                    contextPlan={contextPlan}
                />
            )}

            {step === "payment" && effectiveSelectedIds.length > 0 && (
                <PaymentChoiceStep
                    mode={mode}
                    selectedIds={effectiveSelectedIds}
                    subscriptions={subscriptions}
                    paymentMethods={paymentMethods}
                    sourcePaymentMethodId={sourcePaymentMethodId}
                    contextPlan={contextPlan}
                    cardOnly={cardOnly}
                    onSuccess={() => { setStep( "success" ); onSuccess(); }}
                    onBack={skipPlanSelect ? onHide : () => setStep( "select" )}
                />
            )}

            {step === "success" && <SuccessStep onDone={onHide} />}
        </Modal>
    );
}
